import type { IMergeFieldToken } from '@levitate/rich-text';
import {
	CityPlaceholder,
	CompanyNamePlaceholder,
	FirstNamePlaceholder,
	FullNamePlaceholder,
	JobTitlePlaceholder,
	LastNamePlaceholder,
	StatePlaceholder,
} from '@Models/Token';

/**
 * Meeting-note merge fields. Meeting/sender/action tokens are ported verbatim from the legacy
 * TinyMCE `meetingmergefieldmenu` (RichContentDocumentEditor/plugins.tsx) as raw-mode tokens — each
 * `symbol` is the exact literal the menu inserted and the backend resolves at send-time. Contact
 * fields are value-mode (they carry a `value`), matching the `data-placeholder` spans the default
 * meeting-note template already stores. Keeping both forms keeps saved notes byte-compatible.
 */
interface IMeetingMergeField {
	/** Pill label shown in the editor — self-describing so, e.g., a sender vs. meeting "First Name" differ. */
	text: string;
	symbol: string;
	/** Present for contact/generic fields: serialize as a `data-placeholder` span, not raw braces. */
	value?: string;
	/** Dropdown label; defaults to `text`. Keeps the grouped menu short while the pill stays explicit. */
	menuLabel?: string;
}

interface IMeetingMergeFieldGroup {
	label: string;
	fields: readonly IMeetingMergeField[];
}

const CONTACT_FIELDS: readonly IMeetingMergeField[] = [
	FirstNamePlaceholder,
	LastNamePlaceholder,
	FullNamePlaceholder,
	CompanyNamePlaceholder,
	JobTitlePlaceholder,
	CityPlaceholder,
	StatePlaceholder,
].map(placeholder => ({ text: placeholder.text, symbol: placeholder.symbol, value: placeholder.value }));

const GROUPS: readonly IMeetingMergeFieldGroup[] = [
	{ label: 'Contact', fields: CONTACT_FIELDS },
	{
		label: 'Meeting Booking Details',
		fields: [
			{ menuLabel: 'First Name', text: 'Meeting First Name', symbol: '{{meeting first name}}' },
			{ menuLabel: 'Last Name', text: 'Meeting Last Name', symbol: '{{meeting last name}}' },
			{ menuLabel: 'Phone Number', text: 'Meeting Phone Number', symbol: '{{meeting phone number}}' },
			{ menuLabel: 'Company Name', text: 'Meeting Company Name', symbol: '{{meeting company name}}' },
			{ menuLabel: 'Email Address', text: 'Meeting Email Address', symbol: '{{meeting email address}}' },
			{ menuLabel: 'Comments', text: 'Meeting Comments', symbol: '{{meeting comments}}' },
			{ menuLabel: 'Display Name', text: 'Meeting Display Name', symbol: '{{meeting display name}}' },
		],
	},
	{
		label: 'Meeting Schedule',
		fields: [
			{ text: 'Meeting Day', symbol: '{{meeting day}}' },
			{ text: 'Meeting Date', symbol: '{{meeting date}}' },
			{ text: 'Meeting Time', symbol: '{{meeting time}}' },
			{ text: 'Time Zone', symbol: '{{meeting time zone}}' },
			{ text: 'Virtual Meeting Link', symbol: '{{virtual meeting link}}' },
			{ text: 'Meeting Location', symbol: '{{meeting location}}' },
		],
	},
	{
		label: 'Action Links',
		fields: [
			{ text: 'Reschedule Link', symbol: '{{reschedule link}}' },
			{ text: 'Cancel Link', symbol: '{{meeting cancel}}' },
			{ text: 'Meeting Homepage Link', symbol: '{{meeting homepage link}}' },
		],
	},
	{
		label: 'Sender Information',
		fields: [
			{ menuLabel: 'First Name', text: 'Sender First Name', symbol: '{{sender first name}}' },
			{ menuLabel: 'Full Name', text: 'Sender Full Name', symbol: '{{sender full name}}' },
			{ menuLabel: 'Email', text: 'Sender Email', symbol: '{{sender email}}' },
			{ menuLabel: 'Phone Number', text: 'Sender Phone Number', symbol: '{{sender phone number}}' },
		],
	},
];

/** Grouped fields for the editor's "Merge field" dropdown. */
export const MEETING_MERGE_FIELD_GROUPS: readonly IMeetingMergeFieldGroup[] = GROUPS;

/** Flat token list used to convert stored raw `{{...}}` text into editor pills on load. */
export const MEETING_MERGE_FIELD_TOKENS: readonly IMergeFieldToken[] = GROUPS.flatMap(group => group.fields);
